import React, { useEffect, useRef } from "react";
import { CustomerDetailsDto, CustomerUpdateRequest, EmailFileNameOption, EmailFormat } from "interfaces/Mueller.Business.Models";
import { MCard } from "components/Shared";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_ACCOUNT_EDIT } from "typeConstants/rights";
import { getCustomerDetails } from "actions/customer";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_EMAIL } from "typeConstants/dataTypes";
import { updateCustomer } from "requests/customer";

interface Props {
  customerDetails: CustomerDetailsDto;
  canEditAccountDetails: boolean;
  getCustomerDetails: (customerNumber: number) => {};
}

export const EmailFileNameOptions: Partial<EmailFileNameOption>[] = [
  { id: 1, code: "PolicyNumber", displayName: "Policy Number" },
  { id: 2, code: "NumericPolicyNumber", displayName: "Numeric Policy Number" },
  { id: 3, code: "AgentCodePolicyNumber", displayName: "Agent and Policy Number" },
  { id: 4, code: "SubAgentNumWdashNameHaz", displayName: "Agent Number w/-, Name, Hazard Desc." },
  { id: 6, code: "SubAgentNumWdashName", displayName: "Agent Number w/-, Name" },
  { id: 7, code: "MessageSubject", displayName: "Message Subject" },
  { id: 8, code: "PolicyNumberWdashName", displayName: "Policy Number w/- Name" },
];

export const EmailFileNameOptionsDropDown = EmailFileNameOptions.map(efn => ({
  label: efn.displayName,
  value: efn.code,
}));

export const EmailFormats: Partial<EmailFormat>[] = [
  { id: 1, name: "Standard Format", code: "STANDARD_FORMAT", methodName: "GetStandardFormat", displayName: "Report PDF, REC letter" },
  { id: 2, name: "Custom Format", code: "CUSTOM_FORMAT", methodName: "GetCustomFormat", displayName: "Report PDF, Images, REC letter" },
  { id: 3, name: "NoReportPDF", code: "NO_REPORT_PDF", methodName: "GetNoAttachments", displayName: "No Report PDF" },
];

export const EmailFormatsDropDown = EmailFormats.map(ef => ({
  label: ef.displayName,
  value: ef.code,
}));

export const mapCustomerDetailsToCustomerUpdate = (customerDetails: CustomerDetailsDto): CustomerUpdateRequest => ({
  customerNumber: customerDetails.number,
  name: customerDetails.name,
  displayName: customerDetails.displayName,
  companyId: customerDetails.majorCompany.id,
  address1: customerDetails.mailingAddress.address1,
  address2: customerDetails.mailingAddress.address2,
  city: customerDetails.mailingAddress.city,
  stateAbbreviation: customerDetails.mailingAddress.state,
  zip: customerDetails.mailingAddress.zip,
  email: customerDetails.email,
  phoneNumber1: customerDetails.phoneNumber1,
  phoneNumber2: customerDetails.phoneNumber2,
  customerTypeId: customerDetails.customerTypeId,
  contractEndDate: customerDetails.contractEndDate,
  inactiveDate: customerDetails.inactiveDate,
  emailCC: customerDetails.ccEmailAddress,
  emailBCC: customerDetails.bccEmailAddress,
  emailFileNameOptionCode: customerDetails.pdfFileNameOption?.code,
  emailFormatCode: customerDetails.emailFormat?.code,
});

const CustomerEmailCaseSettings = ({
  customerDetails,
  canEditAccountDetails,
  getCustomerDetails,
} : Props) => {
  const mounted = useRef(true);
  const cardControlMethods = useRef<{ getEditedDataObject: any }>(); // one day MCard will have types and then this wont be an issue

  useEffect(() => () => { mounted.current = false }, []);

  const customerEditFinished = (fieldsChanged) => {
    if (fieldsChanged.length) {
      let postObj = { ...mapCustomerDetailsToCustomerUpdate(customerDetails), ...cardControlMethods.current.getEditedDataObject(true) };
      postObj.customerNumber = customerDetails.number;
      updateCustomerWrapped(postObj);
    }
  };

  const updateCustomerWrapped = (payload) => updateCustomer(customerDetails.number, payload, () => getCustomerDetails(customerDetails.number), mounted);

  // one day mcard will have types
  let fields: any;

  if (customerDetails) {
    fields = [
      {
        key: "email",
        label: "Customer Email",
        value: customerDetails.email,
        editable: true,
        dataType: DATA_TYPE_EMAIL,
        // validation: makeYupValidation(null, /.+@.+\..+/, "Email address is invalid"), // maybe validate for email address, can be blank
      },
      {
        key: "emailCC",
        label: "CC Email Address",
        value: customerDetails.ccEmailAddress,
        editable: true,
        dataType: DATA_TYPE_EMAIL,
      },
      {
        key: "emailBCC",
        label: "BCC Email Address",
        value: customerDetails.bccEmailAddress,
        editable: true,
        dataType: DATA_TYPE_EMAIL,
      },
      {
        key: "emailFileNameOptionCode",
        label: "PDF File Name Option",
        value: EmailFileNameOptionsDropDown.find(ef => ef.value === customerDetails.pdfFileNameOption?.code),
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: EmailFileNameOptionsDropDown },
        editable: true,
      },
      {
        key: "emailFormatCode",
        label: "Email Formats",
        value: EmailFormatsDropDown.find(ef => ef.value === customerDetails.emailFormat?.code),
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: EmailFormatsDropDown },
        editable: true,
      },
    ];
  }

  return <MCard
    className="md-cell md-cell--12"
    isEditable={canEditAccountDetails}
    onEditFinished={customerEditFinished}
    isFetching={!customerDetails}
    title="Case Email Settings"
    getControlMethods={cm => cardControlMethods.current = cm}
    fields={fields}
  />;
};

const mapStateToProps = state => ({
  customerDetails: state.customer.customerDetails,
  canEditAccountDetails: userHasRight(CUSTOMER_ACCOUNT_EDIT)(state),
});

const mapDispatchToProps = dispatch => ({
  getCustomerDetails: (customerNumber) => dispatch(getCustomerDetails(customerNumber)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerEmailCaseSettings);
